/**
 * What a LOOK is, as data.
 *
 * Decision 0055 says a look is a whole presentation the generator emits, and
 * that app-specific behaviour lives in registered modules — *"not string
 * branches scattered through the renderer"*. The renderer obeyed that. The
 * HOST did not: `main.ts` carried 45 comparisons of `state.template` against an
 * app id, deciding one at a time which chrome each look's sidebar, search box,
 * topbar and page head are fed. That is the same violation one layer over,
 * and it is where a seventh app actually costs today — task 0173 step 3.
 *
 * So the per-look constants live here, one record per look, and the host reads
 * a field where it used to compare a string. The test of whether a value
 * belongs in this file is simple and worth applying literally: **if the only
 * thing that decides it is which look is open, it is a field.** A value that
 * also depends on the pool, the person's rights, the view state or what the
 * store holds stays computed at its call site — the record says *whether* and
 * *what*, never *how much of the world to read*.
 *
 * Two things this deliberately is NOT.
 *
 * It is not a token set. Tokens are scalars a component reads (0173's own
 * subject); these are structural choices — does this look's topbar carry a
 * star, does its sidebar disclose a tree. @Codex ruled on the distinction
 * while refereeing 0173: *"Tokens should parameterize components; they must
 * not replace components. The dangerous refactor is not 'too little
 * tokenisation' — it is flattening real topology into one universal component
 * with enough flags to imitate everything."* A field here selects among
 * behaviours the host already has; it does not invent a new one.
 *
 * And it is not complete. The comparisons this file has absorbed are counted
 * by `apps/demo/test/look-records.test.ts`, which ratchets: the count may fall
 * and may not rise. The number going down is the measurement, and it is the
 * one 0173 asks for.
 *
 * The comments travel with the values. Each was written beside the branch it
 * explains, usually recording which verdict or reference reading produced it,
 * and a value moved away from its reason is a value the next session cannot
 * check.
 */
import type { AppTemplate } from '@morph/spec';
import type { InertItem } from '@morph/runtime';
import type { TemplateView } from '@morph/generation';

/** Obsidian's open tab carries the app's ×: chrome, inert (0056). */
const CLOSE_GLYPH = { label: '', glyph: 'M6 6l12 12M18 6L6 18' } as const;

/**
 * The three controls of a macOS window, as three declared COLOURS.
 *
 * Shared between the looks whose reference is a macOS window — Obsidian's and
 * Notion's captures both show them, which is what makes this a component of
 * every look rather than a seventh block in one of them. A look reproducing a
 * different platform's window declares different controls; a look reproducing
 * no window (Fabric, whose title bar carries a name and four glyphs and no
 * lights) declares none.
 *
 * The values are facts about macOS rather than about Morph, which is exactly
 * why they are DECLARED here and not written into the renderer: `inertItem`
 * draws a circle of whatever colour it is handed and knows nothing about
 * windows. Read off the pinned Obsidian capture, at the tab-bar row.
 */
const MAC_WINDOW_CONTROLS = [
  { label: '', dot: '#ff5f57' },
  { label: '', dot: '#febc2e' },
  { label: '', dot: '#28c840' },
] as const;

/** The magnifier, at three of the six. */
const SEARCH_GLYPH = 'M10.5 4a6.5 6.5 0 1 0 0 13a6.5 6.5 0 0 0 0-13zM15.5 15.5L20 20';

export interface LookRecord {
  /* ── the tab strip ─────────────────────────────────────────────────── */
  /** Obsidian's strip: + is a real New note for a writer, and the open tab's
   *  × is the app's chrome. Absent everywhere else. */
  readonly tabs?: { readonly create: true; readonly close: InertItem };

  /* ── the search box ────────────────────────────────────────────────── */
  /** Fibery's box names what it searches: this view. */
  readonly searchPlaceholder?: string;
  /** And what a SCREEN READER is told it searches — the module's own default
   *  is the vault it was born in, which is wrong in every look that has no
   *  vault (@Codex, P2 on `026c328`). Obsidian declares none and keeps it. */
  readonly searchLabel?: string;
  /** Fabric's composer — the panel's heading, its model pill, and "+ Actions",
   *  all inert (0056); the box itself is the library's retrieval. The chip
   *  that says what the composer sees carries a TRUE count, so it is built at
   *  the call site from the library's size rather than named here. */
  readonly assistant?: {
    readonly placeholder: string; readonly heading: string;
    readonly model: string; readonly actions: string;
  };
  /** Notion's view row COLLAPSES its search: the reference fills that row
   *  with ~20px glyphs and a magnifier is one of them, where ours put the
   *  generic 220px box among them (task 0187). The look chooses the
   *  component; the module draws a real button and the host opens the box.
   *  A look that leaves this undeclared keeps the box it always had. */
  readonly searchTrigger?: { readonly glyph: string; readonly label: string };
  /** The mark the look's New button carries — Notion's dropdown chevron.
   *  Inert, inside the real button; see `ViewSwitcherInput.createMark`. */
  readonly createMark?: string;

  /** WHERE THE DOOR LEAVES FOCUS under this look. A look that says nothing
   *  gets the shared first-tool list, which is right for the four looks whose
   *  door opens on a tool.
   *
   *  Word says otherwise, and the difference is real rather than a preference:
   *  its sheet IS the document (`documentIsTheView`), so arriving means
   *  arriving ready to write — the clause has said so since the look was
   *  built. It was reached by accident until 0187's sweep: the shared list
   *  named `.m-wd-tab`, which resolved to the ribbon's INERT File tab, and a
   *  focus that silently did nothing let the canvas place the caret. A
   *  destination that works because the one before it fails is not a
   *  destination; this says it. */
  readonly entryFocus?: string;

  /** WHICH PANE A SIDEBAR OPENS ON, keyed by the sidebar region's spec id —
   *  decision `0068`, from @Codex's finding 2 in the Obsidian viewed
   *  comparison.
   *
   *  A sidebar holds several panes and shows one; `0055` puts the chrome in
   *  the spec and leaves the CHOICE to the host, and the renderer falls back
   *  to the FIRST pane in spec order when the host names none. Obsidian's
   *  right sidebar holds backlinks then the graph, so it opened on backlinks
   *  while the pinned capture shows the graph.
   *
   *  Declared per look rather than branched on in the host: which pane a
   *  named arrangement opens on is a fact about that arrangement, and the
   *  2026-09-03 amendment asks for a row rather than a branch. It is also NOT
   *  a reordering of the spec — backlinks stays first among the panes and
   *  keeps its tab, because `0068` says both remain selectable and another
   *  workspace may legitimately choose either.
   *
   *  KEYED ON WHAT THE SIDEBAR HOLDS, not on its spec id — @Codex's P2 on
   *  `0d83f75`. Read as *"the sidebar that holds a backlinks pane opens on
   *  graph"*. It was `{ 'ws-sidebar': 'graph' }`, an id the current builder
   *  happens to mint, while `templateShortfalls()` matches that region by
   *  purpose and topology; so a valid presentation naming its regions
   *  anything else passed the contract and then opened Backlinks where this
   *  row says Graph. A look's declaration must not depend on a builder's
   *  private naming. */
  readonly opensPaneOver?: Readonly<Record<string, string>>;

  /** Which of the window band's tools SELECT a pane, and which pane each
   *  selects — @Codex's ruling on `2e06944`. A look whose strip is only
   *  commands leaves it out and the tools draw as buttons, which is what
   *  every look but Obsidian does today. */
  readonly ribbonSelects?: Readonly<Record<string, string>>;
  /** THE STRIP'S INVENTORY, so a look may name a tool this repository has
   *  never heard of — task `0204`, and it is the half that was missing rather
   *  than the runtime's. `WorkspaceRibbonInput.tools` has accepted an
   *  inventory since @Codex's P2 on `d80adab`, with a clause proving a
   *  declared `outline` tool is drawn with its own glyph; a HOST calling the
   *  renderer directly could supply one and a LOOK could not, because this
   *  record had no field for it and `main.ts` passed only `selects`. The seam
   *  existed in the runtime and stopped at the demo.
   *
   *  Shaped like `searchTrigger`: a glyph-carrying declaration, all three
   *  parts supplied by the look, nothing resolved against a table here. Left
   *  out, the renderer's default inventory is drawn, which is what every look
   *  with a ribbon does today. */
  readonly ribbonTools?: readonly {
    readonly action: string; readonly label: string; readonly glyph: string;
  }[];

  /* ── the nav sidebar ───────────────────────────────────────────────── */
  /** Fibery's sidebar FOLDS — the thirty-third verdict: the reference's frame
   *  has it folded and the workspace's name in the header. View state keyed by
   *  the look, through the same store as a panel's. */
  readonly navFold?: string;
  /** Notion's tools row beyond Home and Search — chat, calendar, inbox — inert
   *  (0056). No badge on the inbox: a count that is not counted is a lie. */
  readonly navInert?: readonly InertItem[];
  /** The window controls above the sidebar's switcher, where a look whose
   *  reference is a desktop window shows them. */
  readonly navWindow?: readonly InertItem[];
  /** The window's controls in a row of their own, above the columns — for a
   *  look whose shell has no sidebar starting at the window's edge to carry
   *  them. Obsidian's; Notion declares `navWindow` instead because its sidebar
   *  IS the window's leading edge. Same field on the items either way. */
  readonly windowControls?: readonly InertItem[];
  /** What this look calls the section listing the pools. Fibery says Spaces
   *  and marks each with a coloured square the stylesheet draws by position;
   *  Coda's page tree gives the section no heading at all. */
  readonly poolSection: string;
  /** RECENT — the pool's three latest dated objects. Coda's page tree and
   *  Fibery's spaces list do not carry one. */
  readonly recentSection: boolean;
  /** Fibery marks a space by position, not with the pool's emoji. */
  readonly poolIcons: boolean;
  /** Notion's teamspaces DISCLOSE their pages: each row carries the pool's
   *  objects, and which rows are open is view state (0014). Only the open
   *  pool — a twisty over pages the host does not hold would be a dead
   *  control (0055). */
  readonly navTree: boolean;

  /* ── the topbar ────────────────────────────────────────────────────── */
  /** Word's title bar names the document and nothing else that ACTS: its way
   *  out is the ribbon's Close, and it keeps no place history. */
  readonly topbarActions: boolean;
  /** The star, which keeps the page on the hub's shelf. Notion's and
   *  Fibery's; a doc without one (Coda) draws none. */
  readonly topbarStar: boolean;
  /** Fibery's header has no tab strip (the thirty-fourth verdict's first
   *  item): the identity when the sidebar is away, then the end's chrome. */
  readonly topbarFold?: { readonly key: string; readonly workspace: string };
  /** Fabric's bar names the NOTE, not the library: the open item's title, and
   *  no pool icon beside it. */
  readonly topbarTitle: 'pool' | 'open';
  /** Each look's own inert chrome in the bar, by place (0056). */
  readonly topbarInert?: readonly InertItem[];

  /* ── the page head ─────────────────────────────────────────────────── */
  /** WHICH page head, not whether one is fancy. @Codex ruled on this field by
   *  name while refereeing the boundary: *"`pageheadDoc` is topology wearing a
   *  boolean. It does not parameterize one page-head component; it decides
   *  whether Coda receives a structurally different document head containing a
   *  cover, breadcrumb, and pool prose."* A `document` head is three more
   *  regions than a `plain` one, so the record selects the component and the
   *  call site supplies its inputs — which is the same shape `assistant` and
   *  `tabs` already had, and the reason those two were ruled correct. */
  readonly pagehead: 'plain' | 'document';

  /* ── the view bar ──────────────────────────────────────────────────── */
  /** Which views of the collection this look offers. Fibery has two today. */
  readonly views: readonly TemplateView[];
  /** The bar's inert chrome (0056) — Notion's Filter/Sort/bolt/··· endcap
   *  between the views and New, Fibery's Cards/Fields/Filter/Sort/Color row.
   *
   *  A FUNCTION of the open view, not an array, because Fibery's first item
   *  names what the view shows — Rows in the table, Cards on the board. A
   *  record field that had to be an array would have pushed that one label
   *  back into a branch at the call site, which is the thing this file is
   *  for. */
  readonly viewBarInert?: (view: TemplateView) => readonly InertItem[];
  /** Fibery's filter strip. Its chips say only what is TRUE of the view —
   *  the group property's own label, the pool's one type — so the strip is
   *  declared here and its CONTENTS are read from the spec at the call
   *  site. */
  readonly viewBarStrip: boolean;

  /* ── the status bar ────────────────────────────────────────────────── */
  /** Obsidian's bar names the vault and the format; Word's counts the
   *  document and carries the app's own row instead. */
  readonly statusVault: boolean;
  /** Word's status bar beyond the count, inert (0056). No "Page N of M":
   *  the pages are real only in print, and a number that is not measured
   *  would be a lie. */
  readonly statusInert?: readonly InertItem[];

  /* ── the vault ─────────────────────────────────────────────────────── */
  /** Obsidian's vault strip — the switcher, help and settings — and the
   *  pane heads' ×, inert (0056). Before this the strip drew "? ⚙" as text,
   *  from nobody, in the accessibility tree. */
  readonly vaultInert?: {
    readonly vault: readonly InertItem[]; readonly paneClose: InertItem;
  };

  /* ── what the look does with a query, a board and a place ──────────── */
  /** Fabric searches by MEANING: the library's own retrieval index, which
   *  expands a query through the terms the pool uses alongside it. Everyone
   *  else matches a substring over title and prose. */
  readonly searchByMeaning: boolean;
  /** The view row's box filters the BOARD in place — the cards the query
   *  leaves, and the count is what is left. A value change, never a
   *  regeneration (0023): the same page with fewer cards. */
  readonly boardSearch: boolean;
  /** The host hands the board's objects over in the group property's DECLARED
   *  option order — the renderer groups first-seen and never sorts, so
   *  first-seen has to BE the declared order. Distinct from `boardSearch`
   *  though the same two looks want both today: one is what the view row's
   *  box does, the other is what order the columns come out in, and a field
   *  that answered both would make a look unable to ask for one. */
  readonly boardOptionOrder: boolean;
  /** A card opens with its entity's TYPE (Fibery), handed to the stylesheet
   *  as a variable so the module draws it without knowing any pool. */
  readonly typeBadge: boolean;
  /** Back and forward over the places visited under the look, pushed as a
   *  browser does. Leaving the look forgets the history. */
  readonly placeHistory: boolean;
  /** The look's calendar walks month by month and has a Today, so the host
   *  hands over its clock. */
  readonly calendarToday: boolean;
  /** A kept layout reopens on the view it was kept from — only where the
   *  look HAS views to be on. */
  readonly keepsTemplateView: boolean;
  /** The search tool puts the caret in the box ON THE PAGE (the view row's,
   *  the composer's) rather than opening the vault's search pane. */
  readonly searchOnPage: boolean;
  /** Word's sheet IS the document view's module wearing the app's window
   *  (0055): its sections are the same canvases, edited in place — so the
   *  canvases must survive a paint that is not in the document view. */
  readonly documentIsTheView: boolean;
  /** What the door opens ON: one item (Obsidian's note, Fabric's item) or the
   *  whole set (a board, a table, a document of every note).
   *
   *  Found by @Codex, and it is the useful kind of finding: `lookOpensOn()`
   *  compared the template PARAMETER rather than `state.template`, so the
   *  ratchet clause's regex could not see it and reported 0 while six
   *  comparisons stood. The regex is widened in the same commit — a guard
   *  that can only see one spelling of the thing it forbids is green about
   *  the wrong question. */
  readonly opensOn: 'single' | 'large-set';
  /** A kept layout reopens on the view it was starred from; a look with one
   *  view has none to restore. Distinct from `keepsTemplateView`, which is
   *  about what a star RECORDS: this is what a reopen APPLIES, and reading
   *  one for the other is how a field comes to do two jobs. */
  readonly restoresTemplateView: boolean;
}

/** The four views a look offers unless it says otherwise. */
const EVERY_VIEW: readonly TemplateView[] = ['board', 'table', 'calendar', 'timeline'];

/**
 * The registered module a view needs before any switcher may OFFER it.
 *
 * Task `0241`. `views` says what a look CLAIMS; this says what a page can
 * BUILD, and the two are different lists the moment composition puts one
 * look's view part into another look's shell. Measured on
 * `main.e095afc2a770.js`: a Fibery base wearing Notion's `view` part drew four
 * chips, and pressing calendar or timeline wrote `aria-selected="true"` while
 * the stage went on rendering a board — because `app-template.ts`'s Fibery
 * builder clamps anything but `table` to the board. Two chips that lit and
 * changed nothing, which is the decorative dead control `0056` forbids, and
 * an accessibility tree stating a view that is not showing.
 *
 * `board` and `table` are the runtime's own presentations of the same
 * collection and need no module, so they are absent here rather than mapped to
 * nothing. Every other view names the module its template's
 * `APP_TEMPLATE_MODULES` row must carry — so a seventh app answers this with a
 * row and not a branch, and a fifth view cannot arrive as a chip that builds
 * nothing without being added here first.
 */
export const VIEW_MODULE: Readonly<Partial<Record<TemplateView, string>>> =
  { calendar: 'calendar', timeline: 'timeline' };

/** Every look's record. A seventh app adds a row; it does not add a branch. */
export const LOOKS: Readonly<Record<AppTemplate, LookRecord>> = {
  'app.obsidian': {
    tabs: { create: true, close: CLOSE_GLYPH },
    windowControls: MAC_WINDOW_CONTROLS,
    // The right sidebar opens on the GRAPH — decision 0068. It is told from
    // the left one by what it HOLDS: only the right sidebar has a backlinks
    // pane, and the left holds the explorer and the search pane.
    opensPaneOver: { backlinks: 'graph' },
    /* WHICH BAND TOOL CHOOSES WHICH PANE — @Codex's ruling on `2e06944`. The
       band's Files and Search are the sidebar's pane tabs now, so they carry
       `role="tab"` and `aria-selected`, and the duplicate row beneath the band
       goes. Declared here rather than known by the renderer: that `files`
       means the explorer is a fact about the app being reproduced, and a
       seventh app naming its panes differently says so in its own row. */
    ribbonSelects: { files: 'explorer', search: 'search' },
    poolSection: 'Teamspaces', recentSection: true, poolIcons: true, navTree: false,
    topbarActions: true, topbarStar: false, topbarTitle: 'pool', pagehead: 'plain',
    vaultInert: {
      vault: [
        { label: '', glyph: 'M8 5v14M8 5l-3 3M8 5l3 3M16 19V5M16 19l-3-3M16 19l3-3', place: 'start' },
        { label: '', glyph: 'M12 3a9 9 0 1 0 0 18a9 9 0 0 0 0-18zM9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.7.3-1 .8-1 1.5M12 17h.01' },
        { label: '', glyph: 'M12 8a4 4 0 1 0 0 8a4 4 0 0 0 0-8zM12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4' },
      ],
      paneClose: CLOSE_GLYPH,
    },
    views: EVERY_VIEW, viewBarStrip: false, statusVault: true,
    searchByMeaning: false, boardSearch: false, boardOptionOrder: false, typeBadge: false,
    placeHistory: false, calendarToday: false, keepsTemplateView: false,
    searchOnPage: false, documentIsTheView: true,
    opensOn: 'single', restoresTemplateView: false,
  },
  'app.notion': {
    navInert: [
      { label: '', glyph: 'M12 3a9 8 0 0 0-9 8 8 8 0 0 0 3 6l-1 4 4-2a9 8 0 0 0 3 0 9 8 0 0 0 9-8 9 8 0 0 0-9-8z' },
      { label: '', glyph: 'M4 5h16v15H4zM4 10h16M8 3v4M16 3v4M8 14h.01M12 14h.01M16 14h.01' },
      { label: '', glyph: 'M3 13l2-8h14l2 8v6H3zM3 13h5l2 3h4l2-3h5' },
    ],
    // The reference shows the macOS lights at the window's upper left, above
    // the workspace switcher — canon-looks lists them under "Not drawn", which
    // describes the implementation rather than refusing them.
    navWindow: MAC_WINDOW_CONTROLS,
    poolSection: 'Teamspaces', recentSection: true, poolIcons: true, navTree: true,
    topbarActions: true, topbarStar: true, topbarTitle: 'pool',
    topbarInert: [
      // Notion's page actions beyond the star and ···: Share and the comment
      // glyph — inert (0056), before the star as the app has them.
      { label: 'Share', place: 'actions' },
      { label: '', glyph: 'M4 5h16v11H9l-5 4zM8 9h8M8 12h5', place: 'actions' },
    ],
    pagehead: 'plain',
    // THE VIEW ROW'S SEARCH IS A GLYPH (task 0187). The reference's endcap is
    // sort/filter/lightning/SEARCH/expand/settings at ~20px and then the blue
    // New; ours put the generic 220px box among them, the widest thing in the
    // row and the first difference the eye finds. The magnifier is the same
    // path the sidebar's Search tool draws, because it is the same search.
    searchTrigger: { glyph: SEARCH_GLYPH, label: 'Search this view' },
    searchLabel: 'Search this view',
    // The reference's New is a split button with a dropdown chevron.
    createMark: '\u02c5',
    // Notion's endcap: Filter, Sort, the automations bolt, ··· — inert
    // (0056), between the views and New; the search beside them is real.
    viewBarInert: () => [
      { label: 'Filter', glyph: 'M3 5h18l-7 8v6l-4-2v-4z' }, { label: 'Sort', glyph: 'M8 4v16M8 20l-3-3M8 20l3-3M16 20V4M16 4l-3 3M16 4l3 3' },
      { label: '', glyph: 'M13 3L4 14h6l-1 7 9-11h-6z' }, { label: '', glyph: 'M5 12h.01M12 12h.01M19 12h.01' },
    ],
    views: EVERY_VIEW, viewBarStrip: false, statusVault: true,
    searchByMeaning: false, boardSearch: true, boardOptionOrder: true, typeBadge: false,
    placeHistory: true, calendarToday: true, keepsTemplateView: true,
    searchOnPage: true, documentIsTheView: false,
    opensOn: 'large-set', restoresTemplateView: true,
  },
  'app.coda': {
    poolSection: '', recentSection: false, poolIcons: true, navTree: false,
    topbarActions: true, topbarStar: false, topbarTitle: 'pool', pagehead: 'document',
    views: EVERY_VIEW, viewBarStrip: false, statusVault: true,
    searchByMeaning: false, boardSearch: false, boardOptionOrder: false, typeBadge: false,
    placeHistory: true, calendarToday: false, keepsTemplateView: false,
    searchOnPage: false, documentIsTheView: false,
    opensOn: 'large-set', restoresTemplateView: false,
  },
  'app.word': {
    /* The sheet IS the document, so the door arrives at the document.
       NOT `[contenteditable="true"]` — @Codex, P2 on `026c328`. A reader's
       canvas emits ordinary blocks by design, so that selector resolved to
       nothing under `?viewer=reader`: measured, 8 blocks and 0 editable, the
       door leaving focus on `<body>` while its own unresolved request held
       `settleCanvasFocus` shut until the deadline. A block is the
       destination in both grant states; the resolver's fallback gives a
       reader's the `tabindex="-1"` it needs, which is the case that
       fallback was written for. */
    entryFocus: '.m-document-canvas [data-morph-document-block]',
    poolSection: 'Teamspaces', recentSection: true, poolIcons: true, navTree: false,
    topbarActions: false, topbarStar: false, topbarTitle: 'pool',
    topbarInert: [
      // The rest of Word's title bar the reference shows — AutoSave and its
      // toggle, undo and redo, the centred search, Sign in, the window
      // controls — is imagery, not controls (0056).
      { label: 'AutoSave', glyph: 'M4 12a8 8 0 0 1 14-5M20 12a8 8 0 0 1-14 5M18 3v4h-4M6 21v-4h4', place: 'start' },
      { label: 'Off', box: true, place: 'start' },
      { label: '', glyph: 'M9 14L4 9l5-5M4 9h11a5 5 0 0 1 0 10h-2', place: 'start' },
      { label: '', glyph: 'M15 14l5-5-5-5M20 9H9a5 5 0 0 0 0 10h2', place: 'start' },
      { label: 'Search', glyph: SEARCH_GLYPH, box: true, place: 'centre' },
      { label: 'Sign in', box: true, place: 'end' },
      { label: '', glyph: 'M5 12h14', place: 'end' },
      { label: '', glyph: 'M5 5h14v14H5z', place: 'end' },
      { label: '', glyph: 'M6 6l12 12M18 6L6 18', place: 'end' },
    ],
    pagehead: 'plain',
    statusInert: [
      { label: 'English (United States)' }, { label: 'Text Predictions: Off' }, { label: 'Accessibility: Investigate' },
      { label: 'Focus', glyph: 'M4 4h5M15 4h5M4 20h5M15 20h5M4 4v5M4 15v5M20 4v5M20 15v5', place: 'end' },
      { label: '', glyph: 'M4 5h16v14H4z', place: 'end' }, { label: '', glyph: 'M4 5h16v14H4zM12 5v14', place: 'end' },
      { label: '', glyph: 'M4 5h16v14H4zM4 10h16M4 15h16', place: 'end' },
      { label: '−  ────●────  +   100%', place: 'end' },
    ],
    views: EVERY_VIEW, viewBarStrip: false, statusVault: false,
    searchByMeaning: false, boardSearch: false, boardOptionOrder: false, typeBadge: false,
    placeHistory: false, calendarToday: false, keepsTemplateView: false,
    searchOnPage: false, documentIsTheView: true,
    opensOn: 'large-set', restoresTemplateView: false,
  },
  'app.fibery': {
    searchPlaceholder: 'Search this view…', searchLabel: 'Search this view',
    navFold: 'nav:app.fibery',
    poolSection: 'Spaces', recentSection: false, poolIcons: false, navTree: false,
    topbarActions: true, topbarStar: true,
    topbarFold: { key: 'nav:app.fibery', workspace: 'Morph' },
    topbarTitle: 'pool',
    topbarInert: [
      // Fibery's top-right: ››, the workspace search, close — inert (0056).
      { label: '', glyph: 'M6 5l7 7-7 7M13 5l7 7-7 7', place: 'end' },
      { label: '', glyph: SEARCH_GLYPH, place: 'end' },
      { label: '', glyph: 'M6 6l12 12M18 6L6 18', place: 'end' },
    ],
    pagehead: 'plain',
    // Fibery's row carries tools the host cannot back — Cards, Fields,
    // Filter, Sort, Color — inert (0056). The lit ones (`end`) are the
    // reference's Filter and Color.
    viewBarInert: (view) => [
      { label: view === 'table' ? 'Rows' : 'Cards', glyph: 'M4 6h16v12H4zM4 12h16' },
      { label: 'Fields', glyph: 'M2 12s4-6 10-6 10 6 10 6-4 6-10 6S2 12 2 12zM12 9a3 3 0 1 0 0 6a3 3 0 0 0 0-6z' },
      { label: 'Filter •', glyph: 'M3 5h18l-7 8v6l-4-2v-4z', place: 'end' },
      { label: 'Sort', glyph: 'M8 4v16M8 20l-3-3M8 20l3-3M16 20V4M16 4l-3 3M16 4l3 3' },
      { label: 'Color •', glyph: 'M12 3a9 9 0 0 0 0 18c1.5 0 2-1 2-2s-1-2 1-2h2a4 4 0 0 0 4-4 10 10 0 0 0-9-10zM8 8h.01M8 13h.01M12 6h.01M16 8h.01', place: 'end' },
    ],
    views: ['board', 'table'], viewBarStrip: true, statusVault: true,
    searchByMeaning: false, boardSearch: true, boardOptionOrder: true, typeBadge: true,
    placeHistory: true, calendarToday: false, keepsTemplateView: true,
    searchOnPage: true, documentIsTheView: false,
    opensOn: 'large-set', restoresTemplateView: true,
  },
  'app.fabric': {
    assistant: { placeholder: 'Ask me anything…', heading: 'AI assistant',
      model: 'Fabric AI', actions: 'Actions' },
    // The composer asks the LIBRARY, and it is not a vault.
    searchLabel: 'Ask the library',
    poolSection: 'Teamspaces', recentSection: true, poolIcons: true, navTree: false,
    topbarActions: true, topbarStar: false, topbarTitle: 'open',
    topbarInert: [
      // Fabric's title bar, right end: the search glyph and the lilac people
      // button — inert (0056); ··· is real (the way out).
      { label: '', glyph: SEARCH_GLYPH, place: 'end' },
      { label: '', glyph: 'M9 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7zM3 20c0-3.5 2.7-6 6-6s6 2.5 6 6M17 8v6M14 11h6', box: true, place: 'end' },
    ],
    pagehead: 'plain',
    views: EVERY_VIEW, viewBarStrip: false, statusVault: true,
    searchByMeaning: true, boardSearch: false, boardOptionOrder: false, typeBadge: false,
    placeHistory: false, calendarToday: false, keepsTemplateView: false,
    searchOnPage: true, documentIsTheView: false,
    opensOn: 'single', restoresTemplateView: false,
    /* Fabric's first tool is the title bar's search field, and it used to be
       reached by a `.m-app-fabric [data-ws-search]` entry in the SHARED
       first-tool list — the one place a look's NAME still decided a selector
       in `main.ts`, and the same defect @Codex filed against the folded-pane
       refusal on `48121a7`. It moves here because that is what this field is
       for: the comment beside the shared list already said a seventh app
       should answer by declaring rather than by adding a row to a list whose
       ORDER then decides which look wins.

       Behaviour-identical, measured rather than reasoned: in the Fabric door
       none of the shared list's other three selectors resolve at all, the
       list resolves to this one input, and it is the element focus already
       lands on. */
    entryFocus: '[data-ws-search]',
  },
};

/** The open look's record.
 *
 *  A look is undefined while no app template is worn — Morph's own face, which
 *  is not a reproduction of anything. The FALLBACK is Morph's own, not a
 *  borrowed look's: no app chrome, no tree, no star, the plain section
 *  headings. Reading a look's record when none is open is how a refactor
 *  silently gives Morph somebody else's sidebar. */
export const lookOf = (template: AppTemplate | undefined): LookRecord =>
  template === undefined ? MORPH_OWN : LOOKS[template];

const MORPH_OWN: LookRecord = {
  poolSection: 'Teamspaces', recentSection: true, poolIcons: true, navTree: false,
  topbarActions: true, topbarStar: false, topbarTitle: 'pool', pagehead: 'plain',
    views: EVERY_VIEW, viewBarStrip: false, statusVault: true,
    searchByMeaning: false, boardSearch: false, boardOptionOrder: false, typeBadge: false,
    placeHistory: false, calendarToday: false, keepsTemplateView: false,
    searchOnPage: false, documentIsTheView: false,
  opensOn: 'large-set', restoresTemplateView: false,
};
